/**
 * EditActionToggle — unified Edit/Cancel toggle for proposal action surfaces.
 *
 * Icon-only button (pencil / X) used in ActionCardShell, MixedActionExpandedRow,
 * and per-row cells in MixedActionTable so every edit affordance across the
 * proposals UI looks and behaves the same. The meaning of the button is
 * exposed via `aria-label` for screen readers.
 *
 * Built on top of `lib/base/buttons/button` (per FRONTEND_PATTERNS.md —
 * shared UI primitives must come from lib/base).
 */

import { Edit03, XClose } from '@untitledui/icons';
import type { MouseEventHandler } from 'react';
import { memo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

export interface EditActionToggleProps {
  /** Whether the card/row is currently in edit mode (button acts as cancel). */
  isEditing: boolean;
  /** Toggle handler — called when the button is activated. */
  onToggle: () => void;
  /**
   * Accessible label for the button. Overrides the default ("Edit action" /
   * "Cancel editing action"). Pass a contextual label like
   * `Edit 1. Send email` to make the button's purpose clearer to screen readers.
   */
  ariaLabel?: string;
  /**
   * Optional click interceptor. Useful in table rows where the surrounding
   * row listens for clicks — the table can pass a handler that stops
   * propagation before calling `onToggle`.
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Additional classes applied to the button root. */
  className?: string;
  /** Disable the button (e.g. during save). */
  isDisabled?: boolean;
}

/**
 * Default aria-label used when `ariaLabel` isn't provided.
 * Preserves the pre-unification label ("Edit action") so existing
 * accessibility tests continue to pass.
 */
const DEFAULT_EDIT_LABEL = 'Edit action';
const DEFAULT_CANCEL_LABEL = 'Cancel editing action';

export const EditActionToggle = memo(function EditActionToggle({
  isEditing,
  onToggle,
  ariaLabel,
  onClick,
  className,
  isDisabled,
}: EditActionToggleProps) {
  const computedLabel =
    ariaLabel ?? (isEditing ? DEFAULT_CANCEL_LABEL : DEFAULT_EDIT_LABEL);
  const icon = isEditing ? XClose : Edit03;

  // Delegate to onClick if provided (for callers that need stopPropagation),
  // otherwise fall through to onToggle directly.
  const handleClick: MouseEventHandler<HTMLButtonElement> = event => {
    if (onClick) {
      onClick(event);
      if (event.defaultPrevented) {
        return;
      }
    }
    onToggle();
  };

  // Icon-only: rendering no children triggers `data-icon-only` in the base
  // Button, giving a square affordance across all viewports. The meaning of
  // the button comes from `aria-label` + `aria-pressed`.
  return (
    <Button
      size='sm'
      color='tertiary'
      iconLeading={icon}
      aria-pressed={isEditing}
      aria-label={computedLabel}
      onClick={handleClick}
      isDisabled={isDisabled}
      className={cx('shrink-0', className)}
    />
  );
});
